/**
 * Money in ZinqPay is ALWAYS integer minor units (e.g. MKD deni = 1/100 MKD).
 * Never use floats for money. `Minor` is a bigint to make that explicit and safe.
 */
export type Minor = bigint;

export const MKD = 'MKD' as const;
export const MINOR_PER_MAJOR = 100n;

export interface Money {
  amount: Minor;
  currency: string;
}

export function money(amount: Minor, currency: string = MKD): Money {
  return { amount, currency };
}

/** Parse a human major-unit string ("1234.50" or "1234,50") into minor units. */
export function parseMajorToMinor(input: string): Minor {
  const normalized = input.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    throw new Error(`Invalid money amount: "${input}"`);
  }
  const [whole, frac = ''] = normalized.split('.');
  const fracPadded = (frac + '00').slice(0, 2);
  return BigInt(whole!) * MINOR_PER_MAJOR + BigInt(fracPadded);
}

export function addMinor(...values: Minor[]): Minor {
  return values.reduce((acc, v) => acc + v, 0n);
}

export function isPositiveMinor(amount: Minor): boolean {
  return amount > 0n;
}

/**
 * Format minor units in NM convention: "." thousands separator, "," decimal.
 * e.g. 123450n -> "1.234,50"
 */
export function formatMinor(amount: Minor): string {
  const neg = amount < 0n;
  const abs = neg ? -amount : amount;
  const whole = abs / MINOR_PER_MAJOR;
  const frac = abs % MINOR_PER_MAJOR;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const fracStr = frac.toString().padStart(2, '0');
  return `${neg ? '-' : ''}${grouped},${fracStr}`;
}

export function formatMoney(m: Money): string {
  return `${formatMinor(m.amount)} ${m.currency}`;
}
